'use client';

import clsx from 'clsx';
import { Heading2 } from '@/components/section/headings';
import AppLink from '@/components/i18n/AppLink';
import { HighlightIcon } from '@/assets/icons';
import { H3, P, Span } from '@/components/ui';

export type HomeIndustryLink = {
  icon: string;
  label: string;
  href: string;
  text?: string;
};

export type HomeIndustriesSpotlightSectionProps = {
  title: string;
  subtitle?: string;
  items: readonly HomeIndustryLink[];
  viewAllHref: string;
  viewAllLabel: string;
  sectionId?: string;
  className?: string;
};

/** Sector quick links — drives internal linking and crawl paths to industry hubs. */
const HomeIndustriesSpotlightSection: React.FC<HomeIndustriesSpotlightSectionProps> = ({
  title,
  subtitle,
  items,
  viewAllHref,
  viewAllLabel,
  sectionId,
  className,
}) => (
  <section
    id={sectionId}
    className={clsx('container-section', className)}
    aria-labelledby="home-industries-heading"
  >
    <div className="page-heading-wrap" id="home-industries-heading">
      <Heading2
        title={title}
        subtitle={subtitle}
        titleClassName="text-prose"
        subtitleClassName="mt-3 text-prose-muted"
      />
    </div>
    <div className="page-content-grid page-industry-grid">
      {items.map((item) => (
        <AppLink key={item.href} href={item.href} className="page-industry-card group no-underline">
          <Span className="page-highlight-icon !h-10 !w-10 [&_svg]:!h-5 [&_svg]:!w-5" aria-hidden>
            <HighlightIcon name={item.icon} size={20} />
          </Span>
          <div className="min-w-0">
            <H3 className="m-0 text-sm font-semibold text-prose group-hover:text-[var(--color-primary)] sm:text-base">
              {item.label}
            </H3>
            {item.text ? (
              <P className="m-0 mt-1.5 text-xs leading-relaxed text-prose-muted sm:text-sm">{item.text}</P>
            ) : null}
          </div>
        </AppLink>
      ))}
    </div>
    <div className="mt-8 text-center">
      <AppLink
        href={viewAllHref}
        className="text-sm font-semibold text-[var(--color-primary)] underline-offset-4 hover:underline"
      >
        {viewAllLabel}
      </AppLink>
    </div>
  </section>
);

export default HomeIndustriesSpotlightSection;
